import React from 'react';
import { useNavigate } from 'react-router-dom';
import { classNames } from 'primereact/utils';
import { DetailItem } from '../../../types/dashboard';
import { DashboardMetricKey } from '../../../common/enums';
import { dateUtility } from '../../../common/infrastructure/utilities/dateUtility';
import { AppDialog } from '../../../common/components/AppDialog/AppDialog';
import { SkeletonItem } from '../../../common/components/skeletons/SkeletonItem';
import {
  constructPlannerEventHref,
  constructUserHref,
  constructUserPlanHref
} from '../../../common/infrastructure/routes/routePaths';
import styles from './DetailModal.module.scss';

export interface DetailModalProps {
  isOpen: boolean;
  onClose: () => void;
  title: string;
  items?: DetailItem[];
  isLoading?: boolean;
  metricKey?: DashboardMetricKey | null;
}

const getNavigationPath = (item: DetailItem): string | null => {
  if (!item.id) return null;

  if (item.itemType) {
    switch (item.itemType) {
      case 'event':
        return constructPlannerEventHref(item.id);
      case 'user':
        return constructUserHref(item.id);
      case 'subscription':
        return constructUserPlanHref(item.id);
      default:
        return null;
    }
  }

  // Fallback: no navigation if itemType is not provided
  return null;
};

export const DetailModal = React.memo<DetailModalProps>(({
  isOpen,
  onClose,
  title,
  items = [],
  isLoading = false,
  metricKey,
}) => {
  const navigate = useNavigate();

  const handleItemClick = (item: DetailItem) => {
    const path = getNavigationPath(item);
    if (path) {
      navigate(path);
      onClose();
    }
  };

  return (
    <AppDialog
      visible={isOpen}
      onHide={onClose}
      header={title}
      className={styles.detailModal}
      contentClassName={styles.content}
    >
      {isLoading ? (
        <div className={styles.loadingState}>
          {Array.from({ length: 5 }).map((_, index) => (
            <div key={index} className={styles.skeletonItem}>
              <SkeletonItem isLoading={true} value="" width="70%" />
              <SkeletonItem isLoading={true} value="" width="40%" />
            </div>
          ))}
        </div>
      ) : items.length === 0 ? (
        <div className={styles.emptyState}>
          <span className={styles.emptyIcon}>📭</span>
          <p className={styles.emptyText}>Няма налични данни за показване</p>
        </div>
      ) : (
        <div className={styles.itemsList}>
          {items.map((item, index) => (
            <div
              key={index}
              className={classNames(
                styles.item,
                { [styles.clickable]: !!item.id }
              )}
              onClick={() => handleItemClick(item)}
            >
              <div className={styles.itemContent}>
                <span className={styles.itemName}>{item.name}</span>
                {item.dateTime && (
                  <span className={styles.itemDateTime}>
                    {dateUtility.formatDateTime(item.dateTime)}
                  </span>
                )}
                {item.value && (
                  <span className={styles.itemValue}>{item.value}</span>
                )}
              </div>
              {item.id && (
                <span className={styles.itemArrow}>→</span>
              )}
            </div>
          ))}
        </div>
      )}
    </AppDialog>
  );
});